'use client';
import { useEffect, useState } from 'react';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import ErrorIcon from '@/components/customercenter-ui/svgs/notifications/error';
import SearchIcon from '@/components/customercenter-ui/svgs/searchIcon';
import NoImage from '@/customercenter-ui/icons/noImage';
import { useAccount, useCart } from '@/frontastic';
import { ErrorNotification } from '@/helpers/utils/notification';
import { formatDateMoment, OrderDetailsUrl, TranslateText } from '@/helpers/utils/siteUtils';
import {
  HoldOrder,
  OrderByIdPayload,
  OrderHistoryPayload,
} from '@/sdk/composable-commerce/types/payloads/AccountPayloads';
import { DATE_FORMAT, DEFAULT_SITE_LANG } from '../../../../../../constants/constant';
import ContentStackImage from '../../../../common/contentStackImage/contentStackImage';
import { Button } from '../../../ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '../../../ui/card';
import { Table, TableColumnConfig } from '../../../ui/customTable/Table';
import { Input } from '../../../ui/input';
import Loader from '../../../ui/loader';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../ui/tabs';
import { useToast } from '../../../ui/use-toast';
import {
  Hold_Order,
  ORDER_HEADER_KEYS,
  ORDER_RECORD_KEYS,
  ORDER_SECTION_TABS,
  PARAMS_QUERY,
  PAYLOAD_QUERY,
  RESPONSE_SUCCESS_MESSAGE,
} from '../../constants';
import { PageTexts } from '../../i18n';
import { Order, OrderHistory, TrackingInfo, TransformedHoldOrder, TransformedOrder } from '../../types';
import { transformHoldOrders, transformOrderData } from '../../utils';

const OrdersHistory = ({
  orderData,
  showOrderPagination,
  nextOrderDate,
  billingZipCode,
}: {
  orderData: TransformedOrder[];
  showOrderPagination: boolean | undefined;
  nextOrderDate: string | undefined;
  billingZipCode: string;
}) => {
  const { toast } = useToast();
  const router = useRouter();
  const { getOrderHistory, getHoldOrders, getOrderById } = useAccount();
  const { getHoldCart } = useCart(false);
  const [orderDetailsData, setOrderDetailsData] = useState<TransformedOrder[]>([]);
  const [currentSection, setCurrentSection] = useState(ORDER_SECTION_TABS.ORDER_HISTORY);
  const [holdOrders, setHoldOrders] = useState([] as TransformedHoldOrder);
  const [message, setMessage] = useState('');
  const [orderId, setOrderId] = useState('');
  const [loading, setLoading] = useState(false);
  const params = useSearchParams();
  const [showViewMoreButton, setShowViewMoreButton] = useState(false);
  const [orderDate, setOrderDate] = useState<string>();
  const Texts = TranslateText(PageTexts, DEFAULT_SITE_LANG);

  useEffect(() => {
    if (orderData) {
      setOrderDetailsData(orderData);
    }
  }, [orderData]);

  useEffect(() => {
    if (showOrderPagination) {
      setShowViewMoreButton(showOrderPagination);
    }
  }, [showOrderPagination]);

  useEffect(() => {
    if (nextOrderDate) {
      setOrderDate(nextOrderDate);
    }
  }, [nextOrderDate]);

  const handleChange = (value: string) => {
    setOrderId(value);
  };

  const handleSearch = async () => {
    if (!orderId) {
      return getOrderHistoryAsDuration();
    }
    setCurrentSection(ORDER_SECTION_TABS.ORDER_HISTORY);
    setLoading(true);
    const payload = {
      query: {
        divisionCode: params?.get(PAYLOAD_QUERY.DIVISION_CODE) ?? '',
        orderId: orderId ?? '',
        requestOrderLock: '',
        orderZipCode: billingZipCode,
        isExternalOrderId: '',
      },
    } as OrderByIdPayload;
    await getOrderById(payload).then((res) => {
      setLoading(false);
      if (res?.code === RESPONSE_SUCCESS_MESSAGE) {
        setOrderDetailsData(transformOrderData([res?.Order]));
        setMessage('');
      } else {
        setOrderDetailsData([]);
        setMessage(Texts.noRecordsFound);
      }
      setOrderDate('');
      setShowViewMoreButton(false);
    });
  };

  const getOrderHistoryAsDuration = async (nextPage?: boolean) => {
    setLoading(true);
    const payload = {
      divisionCode: params.get(PAYLOAD_QUERY.DIVISION_CODE),
      omsCustomerId: params.get(PAYLOAD_QUERY.OMS_CUSTOMER_ID),
    } as OrderHistoryPayload;
    let orderData = orderDetailsData;
    if (orderDate && nextPage) {
      payload['nextOrderDate'] = orderDate;
    } else {
      orderData = [];
    }
    await getOrderHistory(payload)
      .then((res: OrderHistory) => {
        setOrderDetailsData([...orderData, ...transformOrderData(res?.Orders || ([] as Order[]))]);
        setShowViewMoreButton(res?.ResultMetaData?.hasMoreOrders);
        setOrderDate(res?.ResultMetaData?.nextOrderDate);
        if (res?.Orders?.length > 0) {
          setMessage('');
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  const columnsData: TableColumnConfig<any>[] = [
    {
      key: ORDER_RECORD_KEYS.ORDER_ID,
      header: ORDER_HEADER_KEYS.ORDER_ID,
      render: (data, rowData) => {
        const orderId = data;
        const divisionCode = params.get(PARAMS_QUERY.DIVISION_CODE) ?? '';
        return (
          <>
            {rowData?.statusDescription?.statusDescriptions !== Hold_Order ? (
              <button
                className="gbmask font-medium text-blueLight underline"
                onClick={() => {
                  if (!orderId || !divisionCode) {
                    ErrorNotification(toast, {
                      description: `Bad request undefined data ORDER-ID:: ${orderId} DIVISION-CODE:: ${divisionCode}`,
                      action: <ErrorIcon className="h-5 w-5" />,
                    });
                  }
                  setLoading(true);
                  const url = OrderDetailsUrl(orderId, divisionCode, '', '', '', true) ?? '';
                  router.push(url);
                }}
              >
                {data}
              </button>
            ) : (
              <Link
                href={'/cart'}
                className="gbmask font-medium text-blueLight underline"
                onClick={async () => {
                  const payload = {
                    query: {
                      cartId: rowData?.id,
                    },
                  };
                  await getHoldCart(payload);
                }}
              >
                {data}
              </Link>
            )}
          </>
        );
      },
    },
    {
      key: ORDER_RECORD_KEYS.ORDER_DATE,
      header: ORDER_HEADER_KEYS.ORDER_DATE,
    },
    {
      key: ORDER_RECORD_KEYS.STATUS,
      header: ORDER_HEADER_KEYS.STATUS,
      render: (data) => (
        <div className="flex flex-col font-medium">
          <div>{data?.statusDescriptions}</div>
          <section className="mb-1 mt-3 flex flex-col gap-3">
            {data?.trackingDetails?.length > 0 &&
              data?.trackingDetails?.map((trackingDetail: TrackingInfo) => {
                const trackingLink = trackingDetail?.trackingLink ?? '';
                const trackingCode = trackingDetail?.trackingCode ?? '';
                const externalTrackingLink = trackingDetail?.externalTrackingLink;
                return (
                  <section key={trackingCode}>
                    <button
                      type="button"
                      className="h-[39px] w-[151px] rounded-md border-[1px] border-foggyGray px-3 py-1.5 text-[15px] font-medium"
                      onClick={() => {
                        const trackingUrl = externalTrackingLink || trackingLink;

                        if (trackingUrl) {
                          window.open(trackingUrl, '_blank', 'noopener,noreferrer');
                        }
                      }}
                    >
                      {Texts?.trackShipmentTitle}
                    </button>
                  </section>
                );
              })}
          </section>
        </div>
      ),
    },
    {
      key: ORDER_RECORD_KEYS.TOTAL,
      header: ORDER_HEADER_KEYS.TOTAL,
      render: (data) => <div>{data}</div>,
    },
    {
      key: ORDER_RECORD_KEYS.TOTAL_ITEMS,
      header: ORDER_HEADER_KEYS.TOTAL_ITEMS,
      render: (data, rowData) => {
        const imageData = rowData?.imageData;
        return (
          <section>
            {imageData?.length > 0 ? (
              <div className="flex items-center gap-2">
                {imageData?.slice(0, 2)?.map((items: any, index: number) => (
                  <section key={`${rowData?.id}-${index}`}>
                    {items?.imageUrl === 'noImage' ? (
                      <NoImage className="h-11 w-11 border-[1px]" width={44} height={44} />
                    ) : (
                      <ContentStackImage
                        className="h-11 w-11 border-[1px]"
                        src={items?.imageUrl}
                        imageWidth={44}
                        imageHeight={44}
                        alt={rowData?.id}
                        loading="eager"
                      />
                    )}
                  </section>
                ))}
                <div className="flex">{imageData?.length > 2 ? <>+ {imageData?.length - 2}</> : null}</div>
              </div>
            ) : (
              <>{data}</>
            )}
          </section>
        );
      },
    },
    {
      key: ORDER_RECORD_KEYS.PAYMENT_METHOD,
      header: ORDER_HEADER_KEYS.PAYMENT_METHOD,
    },
    {
      key: ORDER_RECORD_KEYS.LAST_MODIFIED,
      header: ORDER_HEADER_KEYS.LAST_MODIFIED,
      render: (data) => <div>{formatDateMoment(data, DATE_FORMAT)}</div>,
    },
    {
      key: ORDER_RECORD_KEYS.ORIGIN,
      header: ORDER_HEADER_KEYS.ORIGIN,
    },
  ];

  useEffect(() => {
    if (orderDetailsData?.length === 0) {
      setMessage(Texts.noRecordsFound);
    } else {
      setMessage('');
    }
    const myElement = document.getElementById('message');
    const topPos = myElement?.offsetTop ?? 0;
    const parent = document.getElementById('ScrollSection');
    if (parent) {
      parent.scrollTop = topPos;
    }
  }, [orderDetailsData]);

  const onPageChange = async (nextPage?: boolean) => {
    await getOrderHistoryAsDuration(nextPage);
  };

  const HoldOrders = async () => {
    const payload = {
      query: { omsCustomerId: params.get(PAYLOAD_QUERY.OMS_CUSTOMER_ID) },
    } as HoldOrder;
    const response = await getHoldOrders(payload);
    return response;
  };

  useEffect(() => {
    const getOrders = async () => {
      if (currentSection === ORDER_SECTION_TABS.HOLD_ORDER && holdOrders?.length === 0) {
        setLoading(true);
        const orderResponse = await HoldOrders();
        if (orderResponse?.code === RESPONSE_SUCCESS_MESSAGE) {
          const transformedOrders = transformHoldOrders(orderResponse?.body?.carts?.results);
          setHoldOrders(transformedOrders);
        }
        setLoading(false);
      }
    };
    getOrders();
  }, [currentSection]);

  return (
    <Card className="w-full rounded-md border-foggyGray pb-0 shadow-lg">
      {loading && <Loader />}
      <Tabs
        defaultValue={ORDER_SECTION_TABS.ORDER_HISTORY}
        onValueChange={(value) => setCurrentSection(value)}
        value={currentSection}
      >
        <CardHeader className="p-5">
          <CardTitle className="flex flex-wrap gap-2">
            <TabsList className="mr-auto">
              <TabsTrigger
                value={ORDER_SECTION_TABS.ORDER_HISTORY}
                className="mr-5 px-0 pb-1 text-lg font-normal text-[#656668]"
              >
                Customer Orders
              </TabsTrigger>
              <TabsTrigger
                value={ORDER_SECTION_TABS.HOLD_ORDER}
                className="mr-5 px-0 pb-1 text-lg font-normal text-[#656668]"
              >
                {Texts.holdOrders}
              </TabsTrigger>
            </TabsList>
            <section className="mb-2 flex">
              <div className="relative">
                <Input
                  placeholder="Search Orders"
                  className="relative w-[270px] rounded-md border-[1px] border-foggyGray p-2 text-sm font-medium"
                  onChange={(e) => handleChange(e.target.value)}
                  value={orderId}
                />
                <Button
                  className="absolute right-0 top-0 h-[40px] w-10 rounded-none rounded-r-md bg-[#002C4E] p-0 pl-3 text-3xl"
                  onClick={handleSearch}
                >
                  <span className="mr-3">
                    <SearchIcon></SearchIcon>
                  </span>
                </Button>
              </div>
            </section>
          </CardTitle>
        </CardHeader>
        <TabsContent value={ORDER_SECTION_TABS.ORDER_HISTORY} className="p-5 pt-0">
          <CardContent className="custom-scrollbar max-h-[800px] overflow-y-auto scroll-smooth p-0" id="ScrollSection">
            <Table columns={columnsData} data={orderDetailsData} alignTextTop />
            <div className="text-[14px] font-normal" id="message">
              {message}
            </div>
          </CardContent>
          <CardFooter className="pb-2 pl-0">
            <section className="mt-4 flex w-full justify-start">
              {showViewMoreButton ? (
                <button onClick={() => onPageChange(true)} className="underline">
                  {Texts.viewMoreOrders}
                </button>
              ) : (
                <></>
              )}
            </section>
          </CardFooter>
        </TabsContent>
        <TabsContent value={ORDER_SECTION_TABS.HOLD_ORDER} className="p-5 pt-0">
          <Table columns={columnsData} data={holdOrders} alignTextTop />
          {holdOrders?.length === 0 ? Texts.noRecordsFound : ''}
        </TabsContent>
      </Tabs>
    </Card>
  );
};

export default OrdersHistory;
